import React, { ReactNode } from 'react';
import type { Language } from 'prism-react-renderer';
import Highlight, { defaultProps, PrismTheme } from 'prism-react-renderer';
import darkTheme from 'prism-react-renderer/themes/oceanicNext';
import cx from 'classnames';
import { Box, Flex } from '@chakra-ui/react';
import { CopyButton } from '@components/common';

const WithLineNumbers = ({
  children,
  index,
  lineProps
}: {
  children: ReactNode;
  index: number;
  lineProps: any;
}) => (
  <div
    key={`line${index}`}
    {...lineProps}
    className={cx(lineProps.className, 'table-row')}
  >
    <div className="table-cell pr-3 text-right opacity-50 select-none">
      {index + 1}
    </div>
    {children}
  </div>
);

export function Code({
  className: userClassName,
  language = 'jsx',
  children,
  filename,
  theme,
  highlightedLines,
  codeWithVisibleKeysFormatted,
  withLineNumbers = false,
  withTabs = false,
  withCopy = false,
  showKeysVisibilityToggle = false,
  toggleComponent = null
}: {
  children: string;
  className?: string;
  filename?: string;
  language?: Language;
  theme: string | PrismTheme;
  highlightedLines?: number[];
  withLineNumbers?: boolean;
  withTabs?: boolean;
  withCopy?: boolean;
  codeWithVisibleKeysFormatted?: string;
  showKeysVisibilityToggle?: boolean;
  toggleComponent?: any;
}) {
  return (
    <Box pos="relative">
      <Flex
        align="center"
        gap={2}
        pos="absolute"
        right={0}
        top={0}
        p={4}
        roundedTopRight="md"
        bg="#282c34"
        color="#fff"
      >
        {showKeysVisibilityToggle ? toggleComponent : null}
        {withCopy ? (
          <CopyButton
            copyText={
              typeof codeWithVisibleKeysFormatted === 'string'
                ? codeWithVisibleKeysFormatted
                : children
            }
          />
        ) : null}
      </Flex>

      <Highlight
        {...defaultProps}
        theme={darkTheme}
        code={children}
        language={language}
      >
        {({ className, style, tokens, getLineProps, getTokenProps }) => (
          <pre
            className={cx(
              className,
              'leading-[1.8]',
              userClassName
                ? userClassName
                : 'overflow-x-auto p-4 pr-12 m-6 mx-0 mt-8 bg-white rounded-md text-sm',
              withTabs && 'rounded-tl-none mt-0'
            )}
            style={style}
          >
            {filename ? (
              <span className="block mb-2 text-gray-400">{filename}</span>
            ) : null}
            {tokens.map((line, i) => {
              const lineProps = getLineProps({ line, key: i });
              if (highlightedLines && highlightedLines.includes(i + 1)) {
                lineProps.style = {
                  ...lineProps.style,
                  backgroundColor:
                    theme === 'dark'
                      ? 'rgba(255, 255, 255, 0.1)'
                      : 'rgba(0, 0, 0, 0.1)'
                };
              }

              return withLineNumbers ? (
                <WithLineNumbers
                  key={`line${i}`}
                  index={i}
                  lineProps={lineProps}
                >
                  {line.map((token, key) => {
                    if (token.content !== '' || !token.empty) {
                      return (
                        <span
                          key={`token${key}`}
                          {...getTokenProps({ token, key })}
                        />
                      );
                    }
                  })}
                </WithLineNumbers>
              ) : (
                <Box pl={2} key={`line${i}`} {...lineProps}>
                  {line.map((token, key) => {
                    if (token.content !== '' || !token.empty) {
                      return (
                        <span
                          key={`token${key}`}
                          {...getTokenProps({ token, key })}
                        />
                      );
                    }
                  })}
                </Box>
              );
            })}
          </pre>
        )}
      </Highlight>
    </Box>
  );
}
